<script setup lang="ts">
import type { PropType } from 'vue';
import { onMounted, onUnmounted, getCurrentInstance, computed } from 'vue';
import { Listbox, ListboxLabel, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import Ui2SDropdownSelectItem from './SelectItem.vue';
import type { Ui2FormSelectItem, IconName, UiFormSizes } from '@mop/ui2/types';
import useUi2FormElement from '@mop/ui2/composables/useUi2FormElement';

defineOptions({
  name: 'Ui2DropdownSelect',
});

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  icon: {
    type: String as PropType<IconName>,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  items: {
    type: Array as PropType<Ui2FormSelectItem[]>,
    default: () => [],
  },
  rules: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  highlightRequired: {
    type: Boolean,
    default: false,
  },
  optional: {
    type: Boolean,
    default: false,
  },
  floating: {
    type: Boolean,
    default: false,
  },
  showRadioButtons: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String as PropType<UiFormSizes>,
    default: 'md',
  },
  itemSize: {
    type: String as PropType<UiFormSizes>,
    default: 'md',
  },
  showListOnly: {
    type: Boolean,
    default: false,
  },
});

const iconSize = 20;

const { validationErrorsRef, register, unregister, validate } = useUi2FormElement(props);
defineExpose({ validate });
const hasErrorRef = computed(() => validationErrorsRef.value.length > 0);

const selectedItemRef = computed(() => props.items.find((item) => String(item.value) === String(props.modelValue)));

function handleModelUpdate(value: string | number) {
  if (value === props.modelValue) {
    return;
  }
  emit('update:modelValue', value);
  props.rules.length && validate();
}

onMounted(() => {
  register(getCurrentInstance());
});
onUnmounted(() => {
  unregister(getCurrentInstance());
});
</script>

<template>
  <div>
    <Listbox
      :id="id"
      v-slot="{ open }"
      :model-value="modelValue"
      :name="name"
      :disabled="disabled"
      @update:model-value="handleModelUpdate"
    >
      <div
        :class="[
          'ui-dropdown-select',
          `ui-dropdown-select--${size}`,
          {
            'ui-dropdown-select--open': open || showListOnly,
            'ui-dropdown-select--has-value': modelValue,
            'ui-dropdown-select--with-icon': icon,
            'ui-dropdown-select--floating': floating,
            'ui-dropdown-select--disabled': disabled,
            'ui-dropdown-select--error': hasErrorRef,
            'ui-dropdown-select--list-only': showListOnly,
          },
        ]"
      >
        <ListboxLabel v-if="!showListOnly && label" :id="`list-box-label-${id}`" class="ui-dropdown-select__label">
          <span>{{ label }}</span>
          <span v-if="highlightRequired && required" class="ui-dropdown-select__label-required">
            {{ '*' }}
          </span>
          <span v-else-if="optional" class="ui-dropdown-select__label-optional">
            {{ $ui2Config.ui2T('ui.textField.optional') }}
          </span>
        </ListboxLabel>
        <ListboxButton
          v-if="!showListOnly"
          :id="`list-box-button-${id}`"
          class="ui-dropdown-select__button"
          :aria-describedby="hasErrorRef ? `list-box-notification-${id}` : undefined"
          :aria-required="required"
        >
          <div class="ui-dropdown-select__button-content">
            <!-- Keep wrapping div to avoid weird jumping flex behaviour -->
            <div v-show="icon">
              <Ui2Icon v-if="icon" class="ui-dropdown-select__icon" :name="icon" :width="iconSize" :height="iconSize" />
            </div>
            <div class="ui-dropdown-select__text">
              <div v-if="modelValue" class="ui-dropdown-select__text-value">
                <span class="ui-dropdown-select__text-value-text">
                  {{ selectedItemRef?.text ?? modelValue }}
                </span>
                <div
                  v-if="selectedItemRef?.helperText || selectedItemRef?.helperTextIcon"
                  :class="[
                    'ui-dropdown-select__text-value-helper',
                    selectedItemRef.helperTextColor ? `ui-${selectedItemRef.helperTextColor}` : '',
                  ]"
                >
                  <div v-if="selectedItemRef.helperText" class="ui-dropdown-select__text-value-helper-text">
                    {{ selectedItemRef.helperText }}
                  </div>
                  <div v-if="selectedItemRef.helperTextIcon" class="ui-dropdown-select__text-value-helper-icon">
                    <Ui2Icon
                      v-if="selectedItemRef.helperTextIcon"
                      class="ui-dropdown-select__icon"
                      :name="selectedItemRef.helperTextIcon"
                      :width="iconSize"
                      :height="iconSize"
                    />
                  </div>
                </div>
              </div>
              <div v-if="placeholder" class="ui-dropdown-select__text-placeholder">
                {{ placeholder }}
              </div>
            </div>
            <Ui2Icon class="ui-dropdown-select__arrow-icon" name="chevron-down" :width="iconSize" :height="iconSize" />
          </div>
        </ListboxButton>
        <div class="ui-dropdown-select__options-wrapper">
          <ListboxOptions
            :static="showListOnly"
            :class="showListOnly ? 'ui-dropdown__list-only-options' : 'ui-dropdown__options'"
          >
            <ListboxOption
              v-for="(item, index) in items"
              v-slot="{ active, selected }"
              :key="item.value"
              v-bind="item.data"
              :disabled="item.disabled"
              :value="item.value"
              as="li"
              :aria-posinset="index + 1"
              :aria-setsize="items.length"
              :role="showRadioButtons ? 'radio' : 'menuitem'"
            >
              <Ui2SDropdownSelectItem
                :model-value="String(modelValue)"
                :item="item"
                :show-radio-buttons="showRadioButtons"
                :active="active"
                :selected="selected"
                :item-size="itemSize"
                :name="name"
              />
            </ListboxOption>
          </ListboxOptions>
        </div>
        <Ui2Notification
          v-if="!showListOnly && hasErrorRef"
          :id="`list-box-notification-${id}`"
          class="ui-dropdown-select__error-text"
          type="inline"
          :show-icon="false"
          :show-close-button="false"
          status="error"
        >
          {{ validationErrorsRef[0] }}
        </Ui2Notification>
        <div v-if="$slots.helperText" class="ui-dropdown-select__helper-text"><slot name="helperText" /></div>
      </div>
    </Listbox>
  </div>
</template>

<style lang="scss">
@import './ui2dropdown.scss';

$icons-size: 20px;
$button-height: 48px;
$button-height-sm: 40px;
$button-height-floating: 56px;
$button-border-width: 1px;
$button-flex-gap: $space-8;
$select-padding-left: $space-16;

.ui-dropdown-select {
  @include v2-text-style(md);
  width: 100%;
  display: inline-block;
  color: $color-text-body-primary;
  position: relative;

  &:not(.ui-dropdown-select--list-only) {
    .ui-dropdown-select__options-wrapper {
      @include v2-z(global, abovecontent);
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
    }
  }
}

.ui-dropdown-select__button {
  width: 100%;
  border: $button-border-width solid $color-border-primary;
  background: $color-surface-primary;
  border-radius: $border-radius-rounded-md;
  position: relative;
  outline: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  transition: all $animation-duration-medium $animation-type-standard;

  &:hover {
    border-color: $color-border-action-hover;
  }
}

.ui-dropdown-select--disabled {
  cursor: not-allowed;
  pointer-events: none;
  opacity: $v2-disabled-opacity;
}

.ui-dropdown-select__button-content {
  display: flex;
  height: calc($button-height - $button-border-width * 2);
  padding: 0 $select-padding-left;
  align-items: center;
  gap: $button-flex-gap;
}

.ui-dropdown-select__text {
  @include v2-text-style(md);

  position: relative;
  text-align: left;
  flex-grow: 1;
  display: flex;
  align-items: center;
}

.ui-dropdown-select__text-placeholder {
  position: absolute;
  color: $color-text-body-tertiary;
}

.ui-dropdown-select__icon {
  color: $color-text-body-secondary;
}

.ui-dropdown-select__arrow-icon {
  color: $color-text-body-secondary;
  transition: transform $animation-duration-medium $animation-type-standard;
}

.ui-dropdown-select--open {
  .ui-dropdown-select__button {
    border-color: $color-border-focus;
    box-shadow: 0 0 0 1px $color-border-focus;
  }

  .ui-dropdown-select__arrow-icon {
    transform: rotate(-180deg);
  }
}

.ui-dropdown-select__button:focus-visible,
.ui-dropdown-select__button:focus {
  border-color: $color-border-focus;
  box-shadow: 0 0 0 1px $color-border-focus;
}

.ui-dropdown-select--has-value {
  .ui-dropdown-select__text-placeholder {
    display: none;
  }
}

.ui-dropdown__options {
  .ui-dropdown-select__options-wrapper {
    @include v2-z(global, abovecontent);

    position: absolute;
    right: 0;
    left: 0;
    opacity: 0;
    transition: opacity $animation-duration-medium $animation-type-standard;
  }
}

.ui-dropdown-select--open {
  .ui-dropdown-select__options-wrapper {
    opacity: 1;
  }
}

.ui-dropdown-select__option-text {
  flex-grow: 1;
}

.ui-dropdown-select__label {
  @include v2-text-style(sm);
  display: block;
  color: $color-text-body-primary;
  padding-bottom: $space-8;

  display: flex;
  gap: $space-4;
}

.ui-dropdown-select__label-required {
  color: $color-text-error;
}

.ui-dropdown-select__label-optional {
  color: $color-text-body-tertiary;
}

.ui-dropdown-select--sm {
  .ui-dropdown-select__button-content {
    height: calc($button-height-sm - $button-border-width * 2);
  }
}

.ui-dropdown-select__text-value {
  display: flex;
  gap: $space-8;
  align-items: center;
}

.ui-dropdown-select__text-value-text {
  @include v2-text-overflow(1);
}

.ui-dropdown-select__text-value-helper {
  @include v2-text-style(sm);
  display: flex;
  gap: $space-2;
  align-items: center;
}

.ui-dropdown-select--floating {
  .ui-dropdown-select__text-placeholder {
    display: none;
  }

  .ui-dropdown-select__button-content {
    height: calc($button-height-floating - $button-border-width * 2);
  }

  .ui-dropdown-select__label {
    @include v2-z(global, content);
    @include v2-text-style(md);
    color: $color-text-body-secondary;
    position: absolute;
    margin-left: calc($select-padding-left);
    padding: 0;
    top: calc(($button-height-floating + $button-border-width) / 2);
    transform: translateY(-50%);
    transition: all $animation-duration-medium $animation-type-standard;
    pointer-events: none;
  }

  &.ui-dropdown-select--with-icon {
    .ui-dropdown-select__label {
      margin-left: calc($select-padding-left + $button-flex-gap + $icons-size);
    }
  }

  .ui-dropdown-select__text-value {
    color: $color-text-body-primary;
    padding-top: 20px;
  }

  &.ui-dropdown-select--open,
  &.ui-dropdown-select--has-value {
    .ui-dropdown-select__label {
      @include v2-text-style(xs);
      transform: translateY(-22px);
    }
  }
}

.ui-dropdown-select__error-text {
  color: $color-text-error;
  margin-top: $space-8;
}

.ui-dropdown-select__helper-text {
  @include v2-text-style(sm);
  color: $color-text-body-tertiary;
  margin-top: $space-8;
}

.ui-dropdown-select--error {
  .ui-dropdown-select__button {
    border-color: $color-border-error;
    background: $color-surface-error;
  }
  .ui-dropdown-select__label {
    color: $color-text-error;
  }
}
</style>
